import type {
  CreateItineraryComponentRequest,
  ItineraryComponentOut,
  UpdateItineraryComponentRequest,
} from '@utazzo/shared';

/**
 * Local draft helpers for the day-by-day editor. The Days tab edits a local
 * copy of the components; nothing persists until the agent hits Save, which
 * diffs the draft against the server list. New (unsaved) components carry a
 * `tmp_` id so Save knows to create vs update them.
 */

let counter = 0;
export const newLocalId = (): string => `tmp_${Date.now().toString(36)}_${++counter}`;
export const isLocalId = (id: string): boolean => id.startsWith('tmp_');

/** The fields that make up a create/update payload (everything except server-managed ids). */
const PAYLOAD_KEYS = [
  'kind',
  'source',
  'title',
  'descriptionHtml',
  'hotelId',
  'tboHotelCode',
  'cruiseId',
  'cruiseCabinId',
  'airlineId',
  'activityId',
  'activityVariantId',
  'activityTransferMode',
  'transferId',
  'visaId',
  'mealServiceId',
  'supplierId',
  'mealPlanId',
  'roomTypeId',
  'occupancyTypeId',
  'vehicleTypeId',
  'sortOrder',
  'startDate',
  'endDate',
  'startTime',
  'endTime',
  'fromLocation',
  'toLocation',
  'pnr',
  'supplierRef',
  'isRefundable',
  'costPaise',
  'markupPaise',
  'cancellationChargePaise',
  'metaJson',
] as const;

/** Build a full draft component (ItineraryComponentOut shape) from a create payload. */
export const payloadToDraft = (
  payload: CreateItineraryComponentRequest,
  dayId: string | null,
  itineraryId: string,
): ItineraryComponentOut => ({
  id: newLocalId(),
  itineraryId,
  dayId,
  kind: payload.kind,
  source: payload.source ?? 'DB',
  status: 'PROPOSED',
  sortOrder: payload.sortOrder ?? 0,
  title: payload.title,
  descriptionHtml: payload.descriptionHtml ?? null,
  hotelId: payload.hotelId ?? null,
  tboHotelCode: payload.tboHotelCode ?? null,
  cruiseId: payload.cruiseId ?? null,
  cruiseCabinId: payload.cruiseCabinId ?? null,
  airlineId: payload.airlineId ?? null,
  activityId: payload.activityId ?? null,
  activityVariantId: payload.activityVariantId ?? null,
  activityTransferMode: payload.activityTransferMode ?? null,
  transferId: payload.transferId ?? null,
  transferRateId: payload.transferRateId ?? null,
  visaId: payload.visaId ?? null,
  mealServiceId: payload.mealServiceId ?? null,
  supplierId: payload.supplierId ?? null,
  mealPlanId: payload.mealPlanId ?? null,
  roomTypeId: payload.roomTypeId ?? null,
  occupancyTypeId: payload.occupancyTypeId ?? null,
  vehicleTypeId: payload.vehicleTypeId ?? null,
  startDate: payload.startDate ?? null,
  endDate: payload.endDate ?? null,
  startTime: payload.startTime ?? null,
  endTime: payload.endTime ?? null,
  fromLocation: payload.fromLocation ?? null,
  toLocation: payload.toLocation ?? null,
  pnr: payload.pnr ?? null,
  supplierRef: payload.supplierRef ?? null,
  isRefundable: payload.isRefundable ?? true,
  costPaise: payload.costPaise ?? '0',
  markupPaise: payload.markupPaise ?? '0',
  cancellationChargePaise: payload.cancellationChargePaise ?? '0',
  metaJson: payload.metaJson ?? null,
});

/** Extract a create/update payload from a draft component. */
export const draftToPayload = (c: ItineraryComponentOut): CreateItineraryComponentRequest => ({
  kind: c.kind,
  source: c.source,
  title: c.title,
  descriptionHtml: c.descriptionHtml,
  hotelId: c.hotelId,
  tboHotelCode: c.tboHotelCode,
  cruiseId: c.cruiseId,
  cruiseCabinId: c.cruiseCabinId,
  airlineId: c.airlineId,
  activityId: c.activityId,
  activityVariantId: c.activityVariantId,
  activityTransferMode: c.activityTransferMode,
  transferId: c.transferId,
  visaId: c.visaId,
  mealServiceId: c.mealServiceId,
  supplierId: c.supplierId,
  mealPlanId: c.mealPlanId,
  roomTypeId: c.roomTypeId,
  occupancyTypeId: c.occupancyTypeId,
  vehicleTypeId: c.vehicleTypeId,
  sortOrder: c.sortOrder,
  startDate: c.startDate,
  endDate: c.endDate,
  startTime: c.startTime,
  endTime: c.endTime,
  fromLocation: c.fromLocation,
  toLocation: c.toLocation,
  pnr: c.pnr,
  supplierRef: c.supplierRef,
  isRefundable: c.isRefundable,
  costPaise: c.costPaise,
  markupPaise: c.markupPaise,
  cancellationChargePaise: c.cancellationChargePaise,
  metaJson: (c.metaJson ?? null) as Record<string, unknown> | null,
});

/** Apply a partial update (only defined keys) onto a draft component. */
export const applyPatch = (
  c: ItineraryComponentOut,
  patch: Partial<UpdateItineraryComponentRequest>,
): ItineraryComponentOut => {
  const next = { ...c } as Record<string, unknown>;
  for (const k of PAYLOAD_KEYS) {
    const v = (patch as Record<string, unknown>)[k];
    if (v !== undefined) next[k] = v;
  }
  return next as ItineraryComponentOut;
};

/** True when a draft component differs from its saved server version. */
export const isComponentChanged = (
  draft: ItineraryComponentOut,
  server: ItineraryComponentOut,
): boolean =>
  draft.dayId !== server.dayId ||
  JSON.stringify(draftToPayload(draft)) !== JSON.stringify(draftToPayload(server));
